import React, { useRef, useEffect, MutableRefObject } from 'react';
import './RadialScope.css';

interface Props {
  timeDomainRef: MutableRefObject<Float32Array | null>;
}

const SIZE = 400;
const CX = SIZE / 2;
const CY = SIZE / 2;
// Middle-ground radius: visible ring to anchor the eye, but plenty of room to expand
const BASE_R = SIZE * 0.16;
const AMP_SCALE = SIZE * 0.38;
const MAX_R = SIZE * 0.48; // hard clamp — prevents spikes shooting off canvas
const POINTS = 512;
const MIN_SIGNAL = 0.004;

export default function RadialScope({ timeDomainRef }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#07071a';
    ctx.fillRect(0, 0, SIZE, SIZE);

    let animId: number;

    const draw = () => {
      const timeDomain = timeDomainRef.current;

      // Fade trail
      const img = ctx.getImageData(0, 0, SIZE, SIZE);
      const d = img.data;
      for (let i = 0; i < d.length; i += 4) {
        d[i]     = d[i]     * 0.78 | 0;
        d[i + 1] = d[i + 1] * 0.78 | 0;
        d[i + 2] = d[i + 2] * 0.78 | 0;
      }
      ctx.putImageData(img, 0, 0);

      // Reference rings at 25%, 50%, 75%, 100% of AMP_SCALE
      for (let frac = 0.25; frac <= 1.01; frac += 0.25) {
        const ringR = BASE_R + frac * AMP_SCALE * 0.92;
        ctx.beginPath();
        ctx.arc(CX, CY, ringR, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(28, 28, 65, ${0.4 - frac * 0.06})`;
        ctx.lineWidth = 0.6;
        ctx.stroke();
      }

      // Base circle
      ctx.beginPath();
      ctx.arc(CX, CY, BASE_R, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(60, 60, 110, 0.5)';
      ctx.lineWidth = 1;
      ctx.stroke();

      if (timeDomain) {
        let peak = 0;
        let rms = 0;
        for (let i = 0; i < timeDomain.length; i++) {
          const abs = Math.abs(timeDomain[i]);
          if (abs > peak) peak = abs;
          rms += timeDomain[i] * timeDomain[i];
        }
        rms = Math.sqrt(rms / timeDomain.length);

        if (peak > MIN_SIGNAL) {
          // Normalize so peak fills ~80% of AMP_SCALE, capped at 8x gain
          const gain = Math.min(0.80 / peak, 8);
          const energy = Math.min(1, rms * 10);

          const r = 70 + energy * 130 | 0;
          const g = 50 + energy * 90  | 0;
          const b = 190 + energy * 65 | 0;

          const step = Math.max(1, timeDomain.length / POINTS | 0);
          ctx.beginPath();
          for (let i = 0; i < POINTS; i++) {
            const sample = timeDomain[i * step] ?? 0;
            const angle = (i / POINTS) * Math.PI * 2 - Math.PI / 2;
            // Clamp radius: never go below center dot, never exceed canvas
            const rad = Math.min(MAX_R, Math.max(4, BASE_R + sample * gain * AMP_SCALE));
            const x = CX + rad * Math.cos(angle);
            const y = CY + rad * Math.sin(angle);
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          }
          ctx.closePath();
          ctx.strokeStyle = `rgba(${r},${g},${b},0.88)`;
          ctx.lineWidth = 1.5;
          ctx.stroke();

          ctx.beginPath();
          ctx.arc(CX, CY, 4, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${r},${g},${b},0.6)`;
          ctx.fill();
        }
      }

      animId = requestAnimationFrame(draw);
    };

    animId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animId);
  }, [timeDomainRef]);

  return (
    <div className="radial-scope">
      <div className="radial-scope-label">Radial Oscilloscope <span>(shape of sound)</span></div>
      <canvas ref={canvasRef} width={SIZE} height={SIZE} />
    </div>
  );
}
